import { resultRows } from "@/lib/odds-screen/live/derive";
import type { BoardRow, BoardView, EventHeader, FamilySummary, LeagueId, MarketSpec, RowId } from "@/lib/odds-screen/live/types";
import { matchupLabel } from "./labels";
import { clock, groupStart, metaDate } from "./time";
import { NO_STARS, type Stars } from "./starred";

export type SubjectKind = "player" | "team" | "game" | "result";

export const SUBJECT_TITLE: Readonly<Record<SubjectKind, string>> = { player: "Player", team: "Team", game: "Game", result: "Result" };

type FamilyFacts = Pick<FamilySummary, "kind" | "layout" | "markets">;

export type Subject =
  | { readonly kind: "player"; readonly name: string; readonly imageUrl: string | null }
  | { readonly kind: "team"; readonly name: string; readonly event: EventHeader | undefined; readonly league: LeagueId }
  | { readonly kind: "game"; readonly event: EventHeader | undefined }
  | { readonly kind: "result"; readonly name: string };

export function subjectKind(family: FamilyFacts, hasEntity: boolean): SubjectKind {
  if (family.layout === "results") return resultRows(family) === "entity" ? "player" : "result";
  return family.kind === "props" ? "player" : hasEntity ? "team" : "game";
}

export function subjectOf(family: FamilyFacts, row: Pick<BoardRow, "entity" | "player"> | undefined, event: EventHeader | undefined, league: LeagueId): Subject {
  const entity = row?.entity ?? null;
  switch (subjectKind(family, entity !== null)) {
    case "player": return { kind: "player", name: entity?.name ?? "", imageUrl: row?.player?.imageUrl ?? null };
    case "team": return { kind: "team", name: entity?.name ?? "", event, league };
    case "game": return { kind: "game", event };
    case "result": return { kind: "result", name: entity?.name ?? "" };
  }
}

export function rowSpec(family: FamilyFacts, row: Pick<BoardRow, "entity"> | undefined, slot = 0): MarketSpec {
  const spec = family.markets[slot] ?? family.markets[0];
  return family.layout === "results" && row?.entity ? { ...spec, labels: [row.entity.name] } : spec;
}

export type CountNoun = { readonly one: string; readonly many: string };

export function countNoun(family: FamilyFacts): CountNoun {
  return subjectKind(family, true) === "result" ? { one: "result", many: "results" } : { one: "player", many: "players" };
}

export type SubjectShape = { readonly grouping: "event" | "kickoff" | "stickyEvent"; readonly heading: string };

export function subjectShape(family: FamilyFacts): SubjectShape {
  if (family.layout === "results") return { grouping: "stickyEvent", heading: SUBJECT_TITLE[subjectKind(family, true)] };
  return family.kind === "props" ? { grouping: "event", heading: "Player" } : { grouping: "kickoff", heading: "Teams" };
}

function subjectGridCols(compact: boolean, count: number): string {
  const cell = compact ? (count > 2 ? "76px" : "92px") : "minmax(140px,260px)";
  return [compact ? "minmax(0,1fr)" : "minmax(200px,1fr)", ...Array.from({ length: count }, () => cell)].join(" ");
}

export function subjectCols(family: FamilyFacts, compact: boolean): string {
  if (family.layout === "results" && !compact) return "minmax(200px,420px) minmax(140px,220px)";
  return subjectGridCols(compact, family.markets[0].outcomes.length);
}

type EventItem = { readonly kind: "event"; readonly eventId: string; readonly label: string; readonly time: string; readonly count: number; readonly collapsed: boolean };
type KickoffItem = { readonly kind: "kickoff"; readonly startMs: number | null; readonly starred: boolean };
type RowItem = { readonly kind: "row"; readonly rowId: RowId; readonly eventId: string };

export type EventListItem = EventItem | RowItem;
export type KickoffListItem = KickoffItem | RowItem;

export function runsOf<T>(items: readonly T[], keyOf: (item: T) => string): readonly (readonly T[])[] {
  const runs: T[][] = [];
  let previous: string | undefined;
  for (const item of items) {
    const key = keyOf(item);
    if (runs.length > 0 && key === previous) runs[runs.length - 1].push(item);
    else runs.push([item]);
    previous = key;
  }
  return runs;
}

export type EventGroup = {
  readonly eventId: string;
  readonly label: string;
  readonly date: string | null;
  readonly time: string;
  readonly startMs: number;
  readonly rowIds: readonly RowId[];
  readonly collapsed: boolean;
};

export function eventGroups(view: BoardView, rowIds: readonly RowId[], collapsed: ReadonlySet<string>, names: "short" | "full", showDate: boolean, league: LeagueId): readonly EventGroup[] {
  const eventOf = (rowId: RowId) => view.rows.get(rowId)?.eventId ?? "";
  return runsOf(rowIds, eventOf).map((rows) => {
    const eventId = eventOf(rows[0]);
    const event = view.events.get(eventId);
    return {
      eventId,
      label: event ? matchupLabel(event, league, names) : eventId,
      date: event && showDate ? metaDate(event.startMs) : null,
      time: event ? clock(event.startMs) : "",
      startMs: event?.startMs ?? 0,
      rowIds: rows,
      collapsed: collapsed.has(eventId),
    };
  });
}

export function eventItems(view: BoardView, rowIds: readonly RowId[], collapsed: ReadonlySet<string>, compact: boolean, league: LeagueId): readonly EventListItem[] {
  return eventGroups(view, rowIds, collapsed, compact ? "short" : "full", false, league).flatMap((group): EventListItem[] => [
    { kind: "event", eventId: group.eventId, label: group.label, time: group.time, count: group.rowIds.length, collapsed: group.collapsed },
    ...(group.collapsed ? [] : group.rowIds.map((rowId): EventListItem => ({ kind: "row", rowId, eventId: group.eventId }))),
  ]);
}

export type KickoffGroup = { readonly startMs: number; readonly starred: boolean; readonly rowIds: readonly RowId[] };

export function starredKickoffGroups(view: BoardView, rowIds: readonly RowId[], stars: Stars): readonly KickoffGroup[] {
  const startOf = (rowId: RowId) => {
    const ref = view.rows.get(rowId);
    return (ref ? groupStart(view.events.get(ref.eventId)) : undefined) ?? 0;
  };
  const isStarred = (rowId: RowId) => stars.has(view.rows.get(rowId)?.eventId ?? "");
  return [true, false].flatMap((starred) => runsOf(rowIds.filter((rowId) => isStarred(rowId) === starred), (rowId) => String(startOf(rowId)))
    .map((run) => ({ startMs: startOf(run[0]), starred, rowIds: run })));
}

export function kickoffItems(view: BoardView, rowIds: readonly RowId[], stars: Stars = NO_STARS): readonly KickoffListItem[] {
  return starredKickoffGroups(view, rowIds, stars).flatMap((group): KickoffListItem[] => [
    { kind: "kickoff", startMs: group.startMs || null, starred: group.starred },
    ...group.rowIds.map((rowId): KickoffListItem => ({ kind: "row", rowId, eventId: view.rows.get(rowId)?.eventId ?? "" })),
  ]);
}
